import React, { useEffect, useState } from 'react';
import type { Venue } from '../../../types/venue';
import QrCodeGenerator, { generateQrDataUrl } from '../shared/QrCodeGenerator';
import PdfDownloadButtons from '../shared/PdfQrSheet';
import Button from '../../ui/Button';
import { Copy, Check } from 'lucide-react';

interface QrRegeneratePanelProps {
  venue: Venue;
}

const QrRegeneratePanel: React.FC<QrRegeneratePanelProps> = ({ venue }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const venueUrl = `https://whobrew.us/v/${venue.slug}`;

  useEffect(() => {
    generateQrDataUrl(venueUrl, 300)
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [venueUrl]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(venueUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = venueUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">QR Codes</div>
        <h2 className="text-display text-2xl text-coffee-900">QR code and materials</h2>
      </div>

      {/* QR Preview */}
      <div className="flex flex-col items-center p-8 bg-coffee-50 border border-coffee-100 rounded-2xl">
        <QrCodeGenerator url={venueUrl} size={200} />
        <p className="text-sm font-semibold text-coffee-900 mt-4">{venue.name}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs font-mono text-coffee-500 select-all">{venueUrl}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-xs text-coffee-500 hover:text-coffee-800 font-medium transition-colors"
            aria-label="Copy venue URL"
          >
            {copied ? (
              <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copied</>
            ) : (
              <><Copy className="w-3.5 h-3.5" /> Copy</>
            )}
          </button>
        </div>
      </div>

      {/* PDF Downloads */}
      {qrDataUrl && (
        <div>
          <div className="eyebrow mb-3">Download print-ready PDFs</div>
          <PdfDownloadButtons
            businessName={venue.name}
            ssid={venue.wifi?.ssid ?? ''}
            qrDataUrl={qrDataUrl}
            slug={venue.slug}
          />
        </div>
      )}
    </div>
  );
};

export default QrRegeneratePanel;
